"use client";

import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useCallback, useRef, useState, useTransition } from "react";
import ToolLookupCard from "@/components/tools/ToolLookupCard";
import ToolLookupField from "@/components/tools/ToolLookupField";
import ToolMessage from "@/components/tools/ToolMessage";
import ToolTurnstileResults from "@/components/tools/ToolTurnstileResults";
import type { ToolsContent } from "@/lib/content/tools";
import type { ToolDefinition } from "@/lib/content/tools/config";
import { TOOL_EXAMPLES } from "@/lib/content/tools/config";

const TURNSTILE_TIMEOUT_MS = 15000;

export default function ToolTurnstilePanel({
  tool,
  title,
  description,
  field,
  ui,
  turnstileSiteKey,
}: {
  tool: ToolDefinition;
  title: string;
  description: string;
  field: {
    label: string;
    placeholder: string;
  };
  ui: ToolsContent["toolsUi"];
  turnstileSiteKey: string;
}) {
  const turnstileRef = useRef<TurnstileInstance>(null);
  const executingRef = useRef(false);
  const pendingRef = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
    timeoutId: number;
  } | null>(null);

  const [value, setValue] = useState("");
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const clearPending = useCallback((rejectError?: Error) => {
    const pending = pendingRef.current;
    if (!pending) {
      return;
    }

    window.clearTimeout(pending.timeoutId);
    pendingRef.current = null;
    executingRef.current = false;

    if (rejectError) {
      pending.reject(rejectError);
    }
  }, []);

  const requestTurnstileToken = useCallback((): Promise<string> => {
    if (!turnstileSiteKey) {
      return Promise.reject(new Error("turnstile_unavailable"));
    }

    if (executingRef.current && pendingRef.current) {
      return new Promise((resolve, reject) => {
        const existing = pendingRef.current;
        if (!existing) {
          reject(new Error("turnstile_unavailable"));
          return;
        }

        const originalResolve = existing.resolve;
        const originalReject = existing.reject;

        existing.resolve = (token) => {
          originalResolve(token);
          resolve(token);
        };
        existing.reject = (error) => {
          originalReject(error);
          reject(error);
        };
      });
    }

    return new Promise((resolve, reject) => {
      clearPending();

      const timeoutId = window.setTimeout(() => {
        clearPending();
        reject(new Error("turnstile_timeout"));
      }, TURNSTILE_TIMEOUT_MS);

      pendingRef.current = {
        resolve,
        reject,
        timeoutId,
      };

      executingRef.current = true;
      turnstileRef.current?.execute();
    });
  }, [clearPending, turnstileSiteKey]);

  const handleTurnstileSuccess = useCallback((token: string) => {
    const pending = pendingRef.current;
    if (!pending) {
      return;
    }

    window.clearTimeout(pending.timeoutId);
    pendingRef.current = null;
    executingRef.current = false;
    pending.resolve(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    clearPending(new Error("turnstile_failed"));
  }, [clearPending]);

  const handleTurnstileExpire = useCallback(() => {
    clearPending();
    turnstileRef.current?.reset();
  }, [clearPending]);

  async function runLookup(nextValue: string) {
    setError(null);
    setValue(nextValue);

    startTransition(async () => {
      try {
        const token = await requestTurnstileToken();

        const endpoint =
          tool.id === "iban-validation"
            ? "/api/tools/iban"
            : "/api/tools/profanity";

        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ value: nextValue, token }),
        });

        const payload = await response.json();

        if (!response.ok) {
          if (payload.error === "rate_limit") {
            setError(ui.dailyLimit);
          } else if (payload.error === "unauthorized") {
            setError(ui.unauthorized);
          } else {
            setError(ui.requestFailed);
          }
          setResult(null);
          return;
        }

        setResult(payload);
      } catch {
        setError(ui.unauthorized);
        setResult(null);
      } finally {
        turnstileRef.current?.reset();
      }
    });
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void runLookup(value);
  }

  return (
    <>
      <ToolLookupCard title={title} description={description}>
        <form onSubmit={handleSubmit}>
          <ToolLookupField
            id={`${tool.id}-input`}
            name={tool.inputName}
            label={field.label}
            placeholder={field.placeholder}
            submitLabel={
              tool.id === "profanity-detection" ? ui.check : ui.lookup
            }
            loadingLabel={ui.loading}
            isLoading={isPending}
            multiline={tool.inputType === "textarea"}
            value={value}
            onChange={setValue}
            minLength={tool.id === "iban-validation" ? 20 : 2}
            maxLength={tool.id === "iban-validation" ? 32 : 1000}
            disabled={isPending}
            examples={TOOL_EXAMPLES[tool.id]}
            examplesLabel={ui.examples}
            onExampleSelect={(example) => void runLookup(example)}
          />
        </form>
        {turnstileSiteKey ? (
          <Turnstile
            ref={turnstileRef}
            siteKey={turnstileSiteKey}
            options={{
              size: "invisible",
              execution: "execute",
              appearance: "execute",
            }}
            onSuccess={handleTurnstileSuccess}
            onExpire={handleTurnstileExpire}
            onError={handleTurnstileError}
          />
        ) : null}
      </ToolLookupCard>

      {error ? <ToolMessage message={error} /> : null}
      {result ? (
        <ToolTurnstileResults toolId={tool.id} payload={result} ui={ui} />
      ) : null}
    </>
  );
}
